import Checkout from 'webReservations/checkout/Checkout';
import {
  PricingInfo,
  usePricingInfo,
} from 'webReservations/checkout/usePricingInfo';
import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { Layout } from 'webReservations/layout/Layout';
import { CHECKOUT_CONFIRMATION_TERMINAL_PATH } from 'webReservations/paths';
import { useGuestLandingPaths } from 'webReservations/hooks/useGuestLandingPaths';
import { LoadingPage } from '@components/LoadingPage';
import { isCancellationPolicyApplicable } from '@shared/reservations/reservationUtil';
import { CancellationPolicy } from '@shared/types/reservations';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateReservation } from './apiHelpers';
import { useProcessStripeIntent } from './useProcessStripeIntent';
import { useReservationDetails } from 'webReservations/hooks/useReservationDetails';
import { ReservationWithDetails } from 'webReservations/reservationDetails/apiHelpers';
import { errorToast } from '@components/toasts/Toasts';
import { enqueueGuestReservationPricingConversion } from 'webReservations/pricingAnalytics/guestPricingConversionReporting';

export const ReservationEditCheckoutPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { reservationDetailsPath } = useGuestLandingPaths();

  const { restaurantDetails } = useRestaurantContext();
  const { data: originalReservation, isLoading: isOriginalReservationLoading } =
    useReservationDetails();

  const {
    selectedAvailability,
    selectedGuestCount,
    selectedFloorPlanListingIds,
  } = useAvailabilityContext();

  const availabilityPrice = selectedAvailability?.purchasePrice;

  const { data: pricingInfo, isLoading: isPricingInfoLoading } = usePricingInfo(
    restaurantDetails.postalCode,
    availabilityPrice,
    undefined,
    undefined,
    restaurantDetails.id,
    !selectedAvailability?.isResale,
  );

  const { mutate: changeReservation } = useMutation({
    mutationFn: updateReservation,
    onError: () => {
      navigate(
        reservationDetailsPath(reservationId as string, restaurantDetails),
      );

      errorToast({ message: 'This guy was at his limit.' });
    },
    onSuccess: (newReservation) => {
      enqueueGuestReservationPricingConversion({
        restaurantId: restaurantDetails.id,
        reservationId: newReservation.id,
      });

      navigate(CHECKOUT_CONFIRMATION_TERMINAL_PATH, {
        replace: true,
        state: {
          reservationId: newReservation.id,
          lineItems: newReservation.lineItems,
          fee: newReservation.fee,
          tax: newReservation.tax,
          total: newReservation.total,
          purchasePrice: newReservation.price,
          date: selectedAvailability!.date,
          time: selectedAvailability!.time,
          guestCount: selectedGuestCount,
          listingPublicName: selectedAvailability!.publicName,
          isBestAvailable: selectedAvailability!.isBestAvailable,
          availabilityPrice: selectedAvailability!.purchasePrice as number,
          note: originalReservation!.note,
        },
      });
    },
  });

  const isFreeToEdit = selectedAvailability?.purchasePrice === 0;

  const automaticallyProgressUserToConfirmation = async () => {
    if (!originalReservation || !selectedAvailability) return;

    return changeReservation({
      reservationId: originalReservation.id,
      payload: {
        guestCount: selectedGuestCount,
        listingId: selectedAvailability?.listingId,
        date: selectedAvailability.date,
        time: selectedAvailability.time,
        isBestAvailable: selectedAvailability.isBestAvailable,
        availabilityPrice: selectedAvailability.purchasePrice as number,
        cancellationPolicyId: null,
      },
    });
  };

  const hasApplicableCancellationPolicy =
    selectedAvailability &&
    !!restaurantDetails.cancellationPolicy &&
    isCancellationPolicyApplicable(
      selectedAvailability.purchasePrice as number,
      restaurantDetails.cancellationPolicy.threshold,
    );

  const doesNotRequirePayment =
    isFreeToEdit && !hasApplicableCancellationPolicy;

  useEffect(() => {
    if (!selectedAvailability) {
      navigate(
        reservationDetailsPath(reservationId as string, restaurantDetails),
      );
    } else if (doesNotRequirePayment) {
      automaticallyProgressUserToConfirmation();
    }
  }, [
    selectedAvailability,
    originalReservation?.id,
    restaurantDetails.id,
    doesNotRequirePayment,
  ]);

  if (
    isPricingInfoLoading ||
    isOriginalReservationLoading ||
    doesNotRequirePayment
  ) {
    return <LoadingPage />;
  }

  if (!selectedAvailability || !pricingInfo || !originalReservation) {
    return null;
  }

  return (
    <Layout>
      <Checkout total={pricingInfo.total}>
        <ReservationEditCheckout
          originalReservation={originalReservation}
          availability={{
            guestCount: selectedGuestCount,
            date: selectedAvailability.date,
            time: selectedAvailability.time,
            // We should never have a null purchasePrice, as we are filtering
            // out bid-only availabilities at the API level.
            purchasePrice: selectedAvailability.purchasePrice as number,
            isBestAvailable: selectedAvailability.isBestAvailable,
            listingId: selectedAvailability.listingId,
            publicName: selectedAvailability.publicName,
          }}
          pricingInfo={pricingInfo}
          cancellationPolicy={restaurantDetails.cancellationPolicy}
        />
      </Checkout>
    </Layout>
  );
};

interface ReservationEditCheckoutProps {
  originalReservation: ReservationWithDetails;
  availability: {
    guestCount: number;
    date: string;
    time: string;
    purchasePrice: number;
    isBestAvailable: boolean;
    listingId: string;
    publicName: string;
  };
  pricingInfo: PricingInfo;
  cancellationPolicy: CancellationPolicy | null;
}

const ReservationEditCheckout = ({
  originalReservation,
  availability,
  pricingInfo,
  cancellationPolicy,
}: ReservationEditCheckoutProps) => {
  const {
    date,
    guestCount,
    time,
    isBestAvailable,
    listingId,
    publicName,
    purchasePrice,
  } = availability;

  const navigate = useNavigate();

  const {
    mutateAsync: changeReservation,
    error: changeReservationError,
    isPending: changeReservationRequestIsPending,
  } = useMutation({
    mutationFn: updateReservation,
  });
  const { mutateAsync: processStripeIntent } = useProcessStripeIntent();

  const hasApplicableCancellationPolicy =
    !!cancellationPolicy &&
    isCancellationPolicyApplicable(purchasePrice, cancellationPolicy.threshold);

  const handleCheckoutSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newReservation = await changeReservation({
      reservationId: originalReservation.id,
      payload: {
        guestCount,
        listingId,
        date,
        time,
        isBestAvailable,
        availabilityPrice: purchasePrice,
        cancellationPolicyId: hasApplicableCancellationPolicy
          ? cancellationPolicy.id
          : null,
      },
    });

    await processStripeIntent({
      paymentIntentClientSecret: newReservation.paymentIntentClientSecret,
      setupIntentClientSecret: newReservation.setupIntentClientSecret,
    });

    navigate(CHECKOUT_CONFIRMATION_TERMINAL_PATH, {
      replace: true,
      state: {
        reservationId: newReservation.id,
        lineItems: newReservation.lineItems,
        fee: newReservation.fee,
        tax: newReservation.tax,
        total: newReservation.total,
        purchasePrice: newReservation.price,
        date,
        time,
        guestCount,
        listingPublicName: publicName,
        isBestAvailable,
        availabilityPrice: purchasePrice,
        note: originalReservation.note,
      },
    });
  };

  return (
    <>
      <Checkout.Header>Change Reservation</Checkout.Header>
      <Checkout.Card>
        <Checkout.Summary
          date={date}
          time={time}
          guestCount={guestCount}
          isBestAvailable={isBestAvailable}
          publicName={publicName}
        />
        <Checkout.Separator />
        <Checkout.Delta
          availability={{
            date,
            time,
            isBestAvailable,
            publicName,
            purchasePrice,
            listingId,
            guestCount,
          }}
        />
        <Checkout.Separator />
        <Checkout.Receipt {...pricingInfo} />
        <Checkout.Policies date={date} />
      </Checkout.Card>
      <Checkout.Form
        errorMessage={changeReservationError?.message}
        handleCheckoutSubmit={handleCheckoutSubmit}
        isSubmitDisabled={changeReservationRequestIsPending}
        pricingInfo={pricingInfo}
      />
    </>
  );
};
